import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/src/auth/hooks/use-auth";
import { supabase } from "@/src/common/services/supabase.service";
import { messagingService } from "@/src/messaging/services/messaging.service";

interface UseUnreadMessagesCount {
	count: number;
	refresh: () => void;
}

/**
 * Tracks the count of unread messages for the current user. Combines a
 * one-shot fetch at mount with a Supabase Realtime subscription on
 * `public.messages` (filtered on `recipient_id = me`). Realtime needs the
 * table to be in the `supabase_realtime` publication — see migration
 * 20260419010000_enable_realtime_messages.sql.
 *
 * Also exposes `refresh()` for the manual ghost button in the inbox.
 */
export function useUnreadMessagesCount(): UseUnreadMessagesCount {
	const { profile } = useAuth();
	const userId = profile?.id ?? null;
	const [count, setCount] = useState(0);
	const cancelledRef = useRef(false);
	// Channel names must be unique per hook instance — reusing one across
	// mounted consumers makes supabase-js throw on the second subscribe().
	const instanceIdRef = useRef(crypto.randomUUID());

	const fetchCount = useCallback(async () => {
		if (!userId) return;
		try {
			const next = await messagingService.countUnread(userId);
			if (!cancelledRef.current) setCount(next);
		} catch {
			// Network glitches shouldn't clobber an existing count.
		}
	}, [userId]);

	useEffect(() => {
		cancelledRef.current = false;
		if (!userId) {
			setCount(0);
			return;
		}
		void fetchCount();

		const channel = supabase
			.channel(`messages-unread-${userId}-${instanceIdRef.current}`)
			.on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: `recipient_id=eq.${userId}` }, () => {
				void fetchCount();
			})
			.on("postgres_changes", { event: "UPDATE", schema: "public", table: "messages", filter: `recipient_id=eq.${userId}` }, () => {
				void fetchCount();
			})
			.subscribe();

		return () => {
			cancelledRef.current = true;
			void supabase.removeChannel(channel);
		};
	}, [userId, fetchCount]);

	return { count, refresh: fetchCount };
}
